import React, { useState, useEffect } from 'react';
import { Plane, Info } from 'lucide-react';

const FAR117Bar = ({ 
  currentDutyPeriod, 
  currentFlight, 
  timeZoneIndex,
  onClickDetail,
  baseAirport
}) => {
  const [viewMode, setViewMode] = useState('NOW'); // 'NOW' | 'END'
  const [planMode, setPlanMode] = useState(false);
  const [plannedDutyMinutes, setPlannedDutyMinutes] = useState(null);
  const [plannedFlightMinutes, setPlannedFlightMinutes] = useState(null);

  const parseTimeToDate = (timeValue) => {
    if (!timeValue) return null;
    if (timeValue instanceof Date) return timeValue;
    if (typeof timeValue === 'string') {
      const parsed = new Date(timeValue);
      if (!Number.isNaN(parsed.getTime())) return parsed;

      // Handle HH:MM format by anchoring to today
      const [hours, minutes] = timeValue.split(':');
      if (hours !== undefined && minutes !== undefined) {
        const today = new Date();
        const anchored = new Date(today);
        anchored.setHours(Number(hours), Number(minutes), 0, 0);
        return anchored;
      }
      return null;
    }

    if (typeof timeValue === 'object') {
      const zoneTime = timeZoneIndex === 1
        ? timeValue.zulu
        : timeZoneIndex === 2
          ? timeValue.base
          : timeValue.local;

      if (zoneTime) {
        const [hours, minutes] = zoneTime.split(':');
        if (hours !== undefined && minutes !== undefined) {
          const today = new Date();
          const anchored = new Date(today);
          anchored.setHours(Number(hours), Number(minutes), 0, 0);
          return anchored;
        }
      }

      if (timeValue.utc) {
        const parsed = new Date(timeValue.utc);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
    }

    return null;
  };

  const getLegStartTime = (leg) => {
    if (!leg) return null;
    return (
      parseTimeToDate(leg.sched_out?.utc) ||
      parseTimeToDate(leg.sched_out) ||
      parseTimeToDate(leg.scheduled_departure?.zulu) ||
      parseTimeToDate(leg.scheduled_departure) ||
      null
    );
  };

  // Calculate FAR 117 metrics
  const calculateMetrics = () => {
    if (!currentDutyPeriod) {
      return {
        flightMinutes: 0,
        dutyMinutes: 0,
        projectedFlightMinutes: 0,
        projectedDutyMinutes: 0,
        maxFlightMinutes: 540, // 9 hours default
        maxDutyMinutes: 780, // 13 hours default
        expectedStart: 0,
        expectedEnd: 0
      };
    }

    // Current elapsed times
    const now = new Date();
    const reportTime = parseTimeToDate(currentDutyPeriod.report);
    const elapsedDutyMinutes = reportTime
      ? Math.max(0, (now - reportTime) / (1000 * 60))
      : 0;
    
    // Calculate accumulated flight time from completed/in-progress legs
    let accumulatedFlightMinutes = 0;
    if (currentDutyPeriod.legs) {
      currentDutyPeriod.legs.forEach(leg => {
        if (leg.actual_block_minutes) {
          accumulatedFlightMinutes += leg.actual_block_minutes;
        } else if (leg.sched_block_minutes && currentFlight && leg.flight_number === currentFlight.flight_number) {
          // Current flight in progress - estimate based on schedule
          const legStart = getLegStartTime(leg);
          const legMinutesElapsed = legStart ? Math.max(0, (now - legStart) / (1000 * 60)) : 0;
          accumulatedFlightMinutes += Math.min(legMinutesElapsed, leg.sched_block_minutes);
        } else if (leg.actual_block_minutes === 0) {
          // Future leg - don't count yet
        }
      });
    }

    // Projected totals at end of duty period
    const projectedDutyMinutes = currentDutyPeriod.duty_minutes || 780;
    const projectedFlightMinutes = currentDutyPeriod.legs?.reduce((sum, leg) => 
      sum + (leg.sched_block_minutes || 0), 0) || 540;

    // Max limits (could be calculated based on report time and sectors)
    const maxFlightMinutes = 540; // 9 hours
    const maxDutyMinutes = 780; // 13 hours

    // Expected zone (uncertainty window)
    const currentDutyPercent = (elapsedDutyMinutes / maxDutyMinutes) * 100;
    const projectedDutyPercent = (projectedDutyMinutes / maxDutyMinutes) * 100;

    return {
      flightMinutes: accumulatedFlightMinutes,
      dutyMinutes: elapsedDutyMinutes,
      projectedFlightMinutes,
      projectedDutyMinutes,
      maxFlightMinutes,
      maxDutyMinutes,
      expectedStart: currentDutyPercent,
      expectedEnd: projectedDutyPercent
    };
  };

  const metrics = calculateMetrics();

  useEffect(() => {
    if (plannedDutyMinutes === null) {
      setPlannedDutyMinutes(metrics.projectedDutyMinutes);
    }
    if (plannedFlightMinutes === null) {
      // Initialize to 0 additional flight time (showing current state)
      setPlannedFlightMinutes(0);
    }
  }, [metrics.projectedDutyMinutes, plannedDutyMinutes, plannedFlightMinutes]);

  // Determine which values to show based on view mode
  const displayFlightMinutes = viewMode === 'NOW' ? metrics.flightMinutes : metrics.projectedFlightMinutes;
  const displayDutyMinutes = viewMode === 'NOW' ? metrics.dutyMinutes : metrics.projectedDutyMinutes;

  // Calculate percentages for positioning
  const dutyPercent = Math.min((displayDutyMinutes / metrics.maxDutyMinutes) * 100, 100);
  const flightPercent = Math.min((displayFlightMinutes / metrics.maxDutyMinutes) * 100, 100);
  const flightMaxPercent = (metrics.maxFlightMinutes / metrics.maxDutyMinutes) * 100;

  // Determine plane border color based on flight time
  const getPlaneColor = () => {
    const flightPct = (displayFlightMinutes / metrics.maxFlightMinutes) * 100;
    if (flightPct < 75) return '#10b981'; // green
    if (flightPct < 90) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  // Format time display
  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  };

  const getZoneSuffix = () => {
    if (timeZoneIndex === 0) return 'L';
    if (timeZoneIndex === 1) return 'Z';
    return 'B';
  };

  const toggleView = () => {
    setViewMode(prev => prev === 'NOW' ? 'END' : 'NOW');
  };

  const getBaseTimezone = (base) => {
    const timezones = {
      'NYC': 'America/New_York',
      'JFK': 'America/New_York',
      'EWR': 'America/New_York',
      'LAX': 'America/Los_Angeles',
      'SFO': 'America/Los_Angeles',
      'ORD': 'America/Chicago',
      'DFW': 'America/Chicago',
      'ATL': 'America/New_York',
      'MIA': 'America/New_York',
      'SEA': 'America/Los_Angeles',
      'DEN': 'America/Denver',
      'PHX': 'America/Phoenix',
      'LAS': 'America/Los_Angeles',
    };
    return timezones[base] || 'America/New_York';
  };

  const formatTimeInZone = (date, zone) => {
    if (!date || Number.isNaN(date.getTime())) return '--:--';
    if (zone === 'Z') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    }
    if (zone === 'B') {
      const tz = getBaseTimezone(baseAirport || 'NYC');
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
    }
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getLattTimes = (minutes) => {
    const reportTime = parseTimeToDate(currentDutyPeriod?.report);
    if (!reportTime || Number.isNaN(reportTime.getTime())) {
      return { L: '--:--', Z: '--:--', B: '--:--' };
    }
    const target = new Date(reportTime.getTime() + minutes * 60 * 1000);
    return {
      L: formatTimeInZone(target, 'L'),
      Z: formatTimeInZone(target, 'Z'),
      B: formatTimeInZone(target, 'B')
    };
  };

  // Flight time is cumulative block time, not clock-based
  // Use ACTUAL elapsed flight minutes, not projected total
  const currentFlightMinutes = metrics.flightMinutes || 0;
  const maxAdditionalFlightMinutes = Math.max(0, metrics.maxFlightMinutes - currentFlightMinutes);
  const totalFlightMinutes = currentFlightMinutes + (plannedFlightMinutes || 0);

  return (
    <div className="bg-white/[0.03] border-t border-white/10 p-5">
      {/* Header */}
      <div className="relative mb-3">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">
          FAR 117 Duty and Flight Times
        </div>
        <button
          onClick={() => setPlanMode(prev => !prev)}
          className={`absolute left-0 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full transition-colors ${
            planMode ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-gray-300 hover:text-white'
          }`}
          title="Toggle planning mode"
        >
          Plan
        </button>
        <button
          onClick={onClickDetail}
          className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300 hover:text-blue-400 transition-colors"
          title="View detailed breakdown"
        >
          <Info className="w-5 h-5" />
        </button>
      </div>

      {/* Time Displays */}
      <div className="mb-3">
        <div className="flex justify-between gap-3 flex-wrap">
          {/* Flight Time */}
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Flight</span>
            <span className="font-mono text-base font-bold text-green-500 cursor-pointer hover:text-blue-400 transition-colors">
              {formatTime(displayFlightMinutes)}{getZoneSuffix()}
            </span>
            <span className="text-xs text-red-500">/ {formatTime(metrics.maxFlightMinutes)}</span>
          </div>

          {/* Duty Time */}
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Duty</span>
            <span className="font-mono text-base font-bold text-green-500 cursor-pointer hover:text-blue-400 transition-colors">
              {formatTime(displayDutyMinutes)}{getZoneSuffix()}
            </span>
            <span className="text-xs text-red-500">/ {formatTime(metrics.maxDutyMinutes)}</span>
          </div>

          {/* Expected/View Toggle */}
          <div className="flex items-baseline gap-1.5">
            <button
              onClick={toggleView}
              className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full transition-colors"
              style={{
                backgroundColor: viewMode === 'NOW' ? '#3b82f6' : '#6b7280',
                color: 'white'
              }}
            >
              {viewMode}
            </button>
            {viewMode === 'END' && (
              <>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Projected</span>
                <span className="font-mono text-base font-bold text-yellow-500">
                  {formatTime(metrics.projectedDutyMinutes)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Combined Bar */}
      <div className="flex flex-col gap-1.5">
        {/* Bar Track */}
        <div className="relative h-8 bg-black/15 rounded-2xl border border-white/10 overflow-visible">
          {/* Duty Fill (Moving Bar) */}
          <div
            className="absolute left-0 top-0 h-full rounded-2xl transition-all duration-300 ease-out"
            style={{
              width: `${dutyPercent}%`,
              background: 'linear-gradient(90deg, #10b981 0%, #8b5cf6 55%, #f59e0b 80%, #ef4444 100%)'
            }}
          />

          {/* Expected Zone (only in NOW view) */}
          {viewMode === 'NOW' && metrics.expectedEnd > metrics.expectedStart && (
            <div
              className="absolute top-0 h-full pointer-events-none"
              style={{
                left: `${metrics.expectedStart}%`,
                width: `${metrics.expectedEnd - metrics.expectedStart}%`,
                background: `repeating-linear-gradient(
                  135deg,
                  rgba(255, 255, 255, 0.18) 0,
                  rgba(255, 255, 255, 0.18) 6px,
                  rgba(255, 255, 255, 0.04) 6px,
                  rgba(255, 255, 255, 0.04) 12px
                )`,
                borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                borderRight: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            />
          )}

          {/* Flight Plane Indicator */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-[26px] h-[26px] rounded-full bg-black/60 flex items-center justify-center text-sm transition-all duration-300 ease-out shadow-lg"
            style={{
              left: `${flightPercent}%`,
              transform: 'translate(-50%, -50%)',
              border: `2px solid ${getPlaneColor()}`
            }}
          >
            ✈️
          </div>

          {/* Planning Markers */}
          {planMode && plannedDutyMinutes !== null && (
            <div
              className="absolute top-0 h-full border-l-2 border-cyan-400/80"
              style={{ left: `${Math.min((plannedDutyMinutes / metrics.maxDutyMinutes) * 100, 100)}%` }}
            />
          )}
          {planMode && plannedFlightMinutes !== null && (
            <div
              className="absolute -bottom-2"
              style={{ left: `${Math.min((totalFlightMinutes / metrics.maxFlightMinutes) * 100, 100)}%`, transform: 'translateX(-50%)' }}
            >
              <div className="text-[9px] text-cyan-300">△</div>
            </div>
          )}

          {/* Max Markers */}
          {/* Flight Max Marker */}
          <div
            className="absolute flex flex-col items-center gap-0.5 pointer-events-none"
            style={{
              left: `${flightMaxPercent}%`,
              top: '-8px',
              transform: 'translateX(-50%)'
            }}
          >
            <div className="w-0.5 h-2 bg-blue-400 rounded-full" />
            <div className="w-px h-10 bg-blue-400/50" />
            <div className="w-0.5 h-2 bg-blue-400 rounded-full" />
          </div>

          {/* Duty Max Marker */}
          <div
            className="absolute flex flex-col items-center gap-0.5 pointer-events-none"
            style={{
              left: '100%',
              top: '-8px',
              transform: 'translateX(-50%)'
            }}
          >
            <div className="w-0.5 h-2 bg-red-400 rounded-full" />
            <div className="w-px h-10 bg-red-400/50" />
            <div className="w-0.5 h-2 bg-red-400 rounded-full" />
          </div>
        </div>

        {/* Scale Labels */}
        <div className="flex justify-between text-[10px] text-gray-500 font-mono px-1">
          <span>0:00</span>
          <span>Flight Max {formatTime(metrics.maxFlightMinutes)}</span>
          <span>Duty Max {formatTime(metrics.maxDutyMinutes)}</span>
        </div>
      </div>

      {/* LATT Times */}
      <div className="mt-2 grid gap-2 text-sm text-gray-300">
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wider text-gray-400 font-semibold">Duty Limit</span>
          {(() => {
            const times = getLattTimes(metrics.maxDutyMinutes || 0);
            return (
              <div className="flex gap-3 font-mono">
                <span>L {times.L}</span>
                <span>Z {times.Z}</span>
                <span>B {times.B}</span>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wider text-gray-400 font-semibold">Flight Limit</span>
          {(() => {
            const times = getLattTimes(metrics.maxFlightMinutes || 0);
            return (
              <div className="flex gap-3 font-mono">
                <span>L {times.L}</span>
                <span>Z {times.Z}</span>
                <span>B {times.B}</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Bar Legend */}
      <div className="text-xs font-semibold text-gray-400 uppercase mt-2 text-center">
        Duty Time (bar) + Flight Time (plane)
      </div>

      {/* Planning Panel */}
      {planMode && (
        <div className="mt-3 border border-cyan-500/30 bg-cyan-500/5 rounded-lg p-3">
          <div className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider mb-2 text-center">
            Delay Simulator (Reference Only)
          </div>

          {/* Duty Slider */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span>Duty Delay (Ground Hold)</span>
              <span className="font-mono text-cyan-200">+{formatTime((plannedDutyMinutes || 0) - metrics.dutyMinutes)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={metrics.maxDutyMinutes}
              value={plannedDutyMinutes ?? 0}
              onChange={(e) => setPlannedDutyMinutes(Number(e.target.value))}
              className="w-full h-2 cursor-pointer accent-cyan-400 bg-gray-700 rounded-lg appearance-none slider"
              style={{
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
            />
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex justify-center gap-3 text-[10px]">
                <span className="text-gray-400">Expires:</span>
                {(() => {
                  const times = getLattTimes(plannedDutyMinutes || 0);
                  return (
                    <>
                      <span className="text-cyan-300">L {times.L}</span>
                      <span className="text-cyan-300">Z {times.Z}</span>
                      <span className="text-cyan-300">B {times.B}</span>
                    </>
                  );
                })()}
              </div>
              <div className="text-center text-[9px] text-gray-500">
                {(plannedDutyMinutes || 0) > metrics.dutyMinutes ? `Simulating +${formatTime((plannedDutyMinutes || 0) - metrics.dutyMinutes)} delay` : 'No delay (current time)'}
              </div>
            </div>
          </div>

          {/* Flight Slider */}
          <div>
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span>Max Taxi Delay (Deice/Hold)</span>
              <span className="font-mono text-cyan-200">+{formatTime(plannedFlightMinutes || 0)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={maxAdditionalFlightMinutes}
              value={plannedFlightMinutes ?? 0}
              onChange={(e) => setPlannedFlightMinutes(Number(e.target.value))}
              className="w-full h-2 cursor-pointer accent-cyan-400 bg-gray-700 rounded-lg appearance-none slider"
              style={{
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
            />
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex justify-center gap-3 text-[10px]">
                <span className="text-cyan-300">Current: {formatTime(currentFlightMinutes)}</span>
                <span className="text-cyan-200 font-semibold">With Delay: {formatTime(totalFlightMinutes)}</span>
                <span className={totalFlightMinutes > metrics.maxFlightMinutes ? "text-red-400 font-semibold" : "text-gray-400"}>
                  Max: {formatTime(metrics.maxFlightMinutes)}
                </span>
              </div>
              <div className="text-center text-[9px] text-gray-500">
                {plannedFlightMinutes > 0 ? `Simulating +${formatTime(plannedFlightMinutes)} taxi/deice delay` : 'No taxi delay (current block time)'}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .slider {
          width: 100%;
          height: 8px;
          border-radius: 5px;
          background: linear-gradient(to right, #374151, #4b5563);
          outline: none;
          -webkit-appearance: none;
          appearance: none;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #06b6d4, #0891b2);
          cursor: pointer;
          box-shadow: 0 0 10px rgba(6, 182, 212, 0.5);
          border: 2px solid rgba(6, 182, 212, 0.8);
          transition: all 0.2s ease;
        }

        .slider::-webkit-slider-thumb:hover {
          width: 22px;
          height: 22px;
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.8);
        }

        .slider::-webkit-slider-thumb:active {
          background: linear-gradient(135deg, #0891b2, #0e7490);
          box-shadow: 0 0 20px rgba(6, 182, 212, 1);
        }

        .slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #06b6d4, #0891b2);
          cursor: pointer;
          box-shadow: 0 0 10px rgba(6, 182, 212, 0.5);
          border: 2px solid rgba(6, 182, 212, 0.8);
          transition: all 0.2s ease;
        }

        .slider::-moz-range-thumb:hover {
          width: 22px;
          height: 22px;
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.8);
        }

        .slider::-moz-range-thumb:active {
          background: linear-gradient(135deg, #0891b2, #0e7490);
          box-shadow: 0 0 20px rgba(6, 182, 212, 1);
        }

        .slider::-moz-range-track {
          background: transparent;
          border: none;
        }

        .slider::-moz-range-progress {
          background: linear-gradient(to right, #06b6d4, #0891b2);
          height: 8px;
          border-radius: 5px;
        }
      `}</style>
    </div>
  );
};

export default FAR117Bar;
